import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthLink as Link } from '../auth/AuthContext';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../api';
import AuthCard from '../components/AuthCard';
import Spinner from '../components/Spinner';

const MIN_PASSWORD_LENGTH = 8;

const ResetPasswordPage = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';

    // 'checking' | 'form' | 'invalid' | 'done'
    const [state, setState] = useState('checking');
    const [password, setPassword] = useState('');
    const [repeat, setRepeat] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) {
            setState('invalid');
            return;
        }
        // Заодно этот запрос приносит куку с CSRF-токеном: без обращения к API её нет,
        // а отправка формы без токена отвергается.
        api.get('/api/auth/password/reset/check', { params: { token } })
            .then(res => setState(res.data.valid ? 'form' : 'invalid'))
            .catch(() => setState('invalid'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Пароль должен быть не короче ${MIN_PASSWORD_LENGTH} символов`);
            return;
        }
        if (password !== repeat) {
            setError('Пароли не совпадают');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await api.post('/api/auth/password/reset', { token, password });
            setState('done');
        } catch (err) {
            setError(err.response?.status === 400
                ? 'Ссылка недействительна или устарела'
                : 'Не удалось сменить пароль. Попробуйте позже.');
        } finally {
            setSaving(false);
        }
    };

    if (state === 'checking') {
        return (
            <AuthCard title="Смена пароля">
                <div className="flex items-center justify-center gap-3 text-gray-500 py-4">
                    <Spinner size={22} /> Проверяем ссылку…
                </div>
            </AuthCard>
        );
    }

    if (state === 'invalid') {
        return (
            <AuthCard title="Ссылка не работает">
                <AlertTriangle size={44} className="text-amber-500 mx-auto mb-4" />
                <p className="text-gray-600 text-sm leading-relaxed">
                    Ссылка для смены пароля устарела или уже использована. Запросите новую -
                    прежний пароль при этом остаётся действующим.
                </p>
                <Link
                    to="/forgot-password"
                    className="mt-6 inline-block bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium no-underline hover:bg-blue-700"
                >
                    Запросить новую ссылку
                </Link>
            </AuthCard>
        );
    }

    if (state === 'done') {
        return (
            <AuthCard title="Пароль изменён">
                <CheckCircle size={44} className="text-emerald-600 mx-auto mb-4" />
                <p className="text-gray-600 text-sm leading-relaxed">
                    Новый пароль сохранён. Вход на других устройствах завершён - там понадобится
                    войти заново.
                </p>
                <Link
                    to="/login"
                    className="mt-6 inline-block bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium no-underline hover:bg-blue-700"
                >
                    Войти
                </Link>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Новый пароль">
            <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                Придумайте пароль не короче {MIN_PASSWORD_LENGTH} символов.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <input
                    className="p-3 border border-gray-300 rounded-lg text-base transition-colors focus:border-blue-500 focus:outline-none disabled:bg-gray-50"
                    type="password"
                    placeholder="Новый пароль"
                    value={password}
                    disabled={saving}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoFocus
                />
                <input
                    className="p-3 border border-gray-300 rounded-lg text-base transition-colors focus:border-blue-500 focus:outline-none disabled:bg-gray-50"
                    type="password"
                    placeholder="Повторите пароль"
                    value={repeat}
                    disabled={saving}
                    onChange={e => setRepeat(e.target.value)}
                    required
                />
                {error && <div className="text-red-600 bg-red-50 p-3 rounded-md text-sm">{error}</div>}
                <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 text-white p-3 border-none rounded-lg text-base font-semibold transition-colors shadow-sm flex items-center justify-center gap-2 enabled:cursor-pointer enabled:hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-default"
                >
                    {saving && <Spinner size={18} />}
                    {saving ? 'Сохраняем…' : 'Сохранить пароль'}
                </button>
            </form>
        </AuthCard>
    );
};

export default ResetPasswordPage;
